import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Button, Form, Input, Spin, Typography } from 'antd'
import { ArrowRightOutlined } from '@ant-design/icons'
import { Logo } from '../../assets/icons/logo'
import {
  LoginLayout,
  SiteLayout,
  ContentLayout,
  LoginForm,
  HeaderContainer,
  LogoContainer,
  Title,
  StyledFormItem,
  ButtonContainer,
  Footer,
} from '../../pages/login/styles'

const layout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
}

const MIN_PASSWORD_LENGTH = 8

export default function InviteAcceptPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = String(searchParams.get('token') || '').trim()

  const [checking, setChecking] = useState(true)
  const [valid, setValid] = useState(false)
  const [meta, setMeta] = useState<{
    email?: string
    organizationName?: string
    officeName?: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!token) {
        setValid(false)
        setError('Ссылка недействительна или истекла')
        setChecking(false)
        return
      }
      try {
        const response = await fetch(
          `/api/home/employees/invite/validate?token=${encodeURIComponent(token)}`,
        )
        const data = await response.json().catch(() => ({}))
        if (cancelled) return
        if (!response.ok) {
          setValid(false)
          setError(data?.errors?.[0]?.detail || 'Ссылка недействительна или истекла')
        } else {
          setValid(true)
          setMeta({
            email: data?.data?.email,
            organizationName: data?.data?.organizationName,
            officeName: data?.data?.officeName,
          })
        }
      } catch {
        if (!cancelled) {
          setValid(false)
          setError('Ошибка сети')
        }
      } finally {
        if (!cancelled) setChecking(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [token])

  const onFinish = async (values: any) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/home/employees/invite/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          name: String(values.name || '').trim(),
          password: values.password,
          password_confirm: values.password_confirm,
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        setError(data?.errors?.[0]?.detail || 'Не удалось завершить регистрацию')
        return
      }
      setDone(true)
    } catch {
      setError('Ошибка сети')
    } finally {
      setLoading(false)
    }
  }

  const currentYear = () => new Date().getFullYear()

  return (
    <LoginLayout>
      <SiteLayout className="site-layout">
        <ContentLayout>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LoginForm {...layout} name="invite-accept" layout="vertical" onFinish={onFinish}>
              <HeaderContainer>
                <LogoContainer>
                  <Logo />
                </LogoContainer>
                <Title>Приглашение в организацию</Title>
                {meta?.organizationName ? (
                  <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    {meta.organizationName}
                    {meta.officeName ? ` · ${meta.officeName}` : ''}
                    {meta.email ? ` · ${meta.email}` : ''}
                  </Typography.Text>
                ) : null}
                {error ? (
                  <Alert style={{ marginTop: 16 }} type="error" showIcon message={error} />
                ) : null}
                {done ? (
                  <Alert
                    style={{ marginTop: 16 }}
                    type="success"
                    showIcon
                    message="Регистрация завершена. Теперь можно войти."
                  />
                ) : null}
              </HeaderContainer>

              {checking && (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <Spin />
                </div>
              )}

              {!checking && valid && !done && (
                <>
                  <StyledFormItem
                    label="ФИО:"
                    name="name"
                    rules={[{ required: true, message: 'Укажите ФИО' }]}
                  >
                    <Input autoComplete="name" />
                  </StyledFormItem>
                  <StyledFormItem
                    label="Пароль:"
                    name="password"
                    rules={[
                      { required: true, message: 'Введите пароль' },
                      { min: MIN_PASSWORD_LENGTH, message: `Минимум ${MIN_PASSWORD_LENGTH} символов` },
                    ]}
                  >
                    <Input.Password autoComplete="new-password" />
                  </StyledFormItem>
                  <StyledFormItem
                    label="Подтверждение пароля:"
                    name="password_confirm"
                    dependencies={['password']}
                    rules={[
                      { required: true, message: 'Подтвердите пароль' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve()
                          }
                          return Promise.reject(new Error('Пароли не совпадают'))
                        },
                      }),
                    ]}
                  >
                    <Input.Password autoComplete="new-password" />
                  </StyledFormItem>
                  <Form.Item>
                    <ButtonContainer>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Завершить регистрацию <ArrowRightOutlined />
                      </Button>
                      <Link to="/login" style={{ fontSize: '90%' }}>
                        На страницу входа
                      </Link>
                    </ButtonContainer>
                  </Form.Item>
                </>
              )}

              {!checking && (!valid || done) && (
                <Form.Item>
                  <ButtonContainer>
                    <Button type="primary" onClick={() => navigate('/login')}>
                      На страницу входа
                    </Button>
                  </ButtonContainer>
                </Form.Item>
              )}
            </LoginForm>
          </div>
        </ContentLayout>
        <Footer>&copy; 2020-{currentYear()} ООО «Аксиома». Все права защищены.</Footer>
      </SiteLayout>
    </LoginLayout>
  )
}
